import type { LucideIcon } from 'lucide-react'
import {
  ArrowLeftRight,
  Compass,
  Home,
  Mail,
  Palette,
  Scale,
  Settings,
  Users,
  Wallet,
} from 'lucide-react'

import type { Messages } from '@/features/locale/messages'

export type NavItem = {
  title: string
  href: string
  icon: LucideIcon
  /** Solo visible con wallet en allowlist interna */
  internal?: boolean
}

export type NavSection = {
  id: string
  label: string
  items: NavItem[]
}

export function buildMobileBottomNavItems(t: Messages): NavItem[] {
  return [
    { title: t.nav.home, href: '/', icon: Home },
    { title: t.nav.portfolio, href: '/portfolio', icon: Wallet },
    { title: t.nav.explore, href: '/explore', icon: Compass },
  ]
}

export function buildMobileDrawerMainNavItems(t: Messages): NavItem[] {
  return [
    { title: t.nav.home, href: '/', icon: Home },
    { title: t.nav.swap, href: '/swap', icon: ArrowLeftRight },
    { title: t.nav.explore, href: '/explore', icon: Compass },
    { title: t.nav.portfolio, href: '/portfolio', icon: Wallet },
  ]
}

export function buildInfoNavItems(t: Messages): NavItem[] {
  return [
    { title: t.nav.about, href: '/about', icon: Users },
    { title: t.nav.legal, href: '/legal', icon: Scale },
    { title: t.nav.contact, href: '/contact', icon: Mail },
  ]
}

export function buildAppNavItems(t: Messages): NavItem[] {
  return [{ title: t.nav.settings, href: '/settings', icon: Settings }]
}

export function buildMobileDrawerSections(t: Messages): NavSection[] {
  return [
    {
      id: 'main',
      label: t.nav.navigation,
      items: buildMobileDrawerMainNavItems(t),
    },
    {
      id: 'app',
      label: t.nav.application,
      items: buildAppNavItems(t),
    },
    {
      id: 'info',
      label: t.nav.information,
      items: buildInfoNavItems(t),
    },
    {
      id: 'internal',
      label: t.nav.internal,
      items: [{ title: t.nav.designSystem, href: '/design-system', icon: Palette, internal: true }],
    },
  ]
}

export const MOBILE_BOTTOM_NAV_HREFS = new Set(['/', '/portfolio', '/explore'])

export function isMobileBottomNavActive(pathname: string, href: string): boolean {
  return isPrimaryNavActive(pathname, href)
}

const PRIMARY_NAV_ROUTE_ALIASES: Record<string, readonly string[]> = {
  '/explore': ['/token', '/liquidity'],
}

export function isPrimaryNavActive(pathname: string, href: string): boolean {
  if (href === '/') {
    return pathname === '/'
  }

  if (pathname === href || pathname.startsWith(`${href}/`)) {
    return true
  }

  const aliases = PRIMARY_NAV_ROUTE_ALIASES[href]
  if (!aliases) return false

  return aliases.some(prefix => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

export function filterNavItemsForMobileDrawer(items: NavItem[]): NavItem[] {
  return items.filter(item => !MOBILE_BOTTOM_NAV_HREFS.has(item.href))
}
